import React, { useCallback } from "react";
import { AutomaticRulesMergePreview } from "@common/dto/financialAccount/mergeFinancialAccounts/merge-financial-accounts-response.dto";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { createRulesUrl } from "@components/settings/Rules/lib/rulesUrl";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";

interface AutomaticRulesPreviewProps {
    automaticRules: AutomaticRulesMergePreview;
    destinationAccountName: string;
}

export const AutomaticRulesPreview: React.FC<AutomaticRulesPreviewProps> = ({
    automaticRules,
    destinationAccountName,
}) => {
    const openPausedRule = useOpenPausedRule(automaticRules);
    if (automaticRules.movedCount === 0 && automaticRules.pausedCount === 0) {
        return null;
    }

    return (
        <div className="mb-4">
            {automaticRules.movedCount > 0 && (
                <p>
                    <strong>
                        {pluralizeWithCount("rule", automaticRules.movedCount)}{" "}
                        will move to {destinationAccountName}
                    </strong>
                    <br />
                    They&apos;ll apply to new transactions only. Existing
                    transactions won&apos;t change.
                </p>
            )}
            {automaticRules.pausedCount > 0 && (
                <p>
                    <strong>
                        {pluralizeWithCount("rule", automaticRules.pausedCount)}{" "}
                        {automaticRules.pausedCount === 1
                            ? "conflicts"
                            : "conflict"}{" "}
                        with an existing rule
                    </strong>
                    <br />
                    We&apos;ll pause{" "}
                    {automaticRules.pausedCount === 1 ? "it" : "them"} so
                    nothing unexpected happens.{" "}
                    <Button variant="link" size="sm" onClick={openPausedRule}>
                        Review conflict
                    </Button>
                </p>
            )}
        </div>
    );
};

export function mergedAccountsMessage(
    automaticRules: AutomaticRulesMergePreview,
): string {
    if (automaticRules.movedCount > 0 && automaticRules.pausedCount > 0) {
        return `Accounts merged. ${pluralizeWithCount("rule", automaticRules.movedCount)} moved and ${automaticRules.pausedCount} ${automaticRules.pausedCount === 1 ? "was" : "were"} paused.`;
    }
    if (automaticRules.movedCount > 0) {
        return `Accounts merged. ${pluralizeWithCount("rule", automaticRules.movedCount)} moved.`;
    }
    if (automaticRules.pausedCount > 0) {
        const verb = automaticRules.pausedCount === 1 ? "was" : "were";
        return `Accounts merged. ${pluralizeWithCount("rule", automaticRules.pausedCount)} ${verb} paused.`;
    }
    return "Accounts merged";
}

export function useOpenPausedRule(automaticRules: AutomaticRulesMergePreview) {
    const navigate = useStateAwareNavigate();
    return useCallback(() => {
        const pausedRule = automaticRules.pausedRules[0];
        if (!pausedRule) {
            return;
        }
        navigate(
            createRulesUrl({
                tab: pausedRule.groupType,
                ruleId: pausedRule.id,
            }),
        );
    }, [automaticRules.pausedRules, navigate]);
}

export function MergedAccountsToast({
    automaticRules,
}: {
    automaticRules: AutomaticRulesMergePreview;
}) {
    const openPausedRule = useOpenPausedRule(automaticRules);
    return (
        <span>
            {mergedAccountsMessage(automaticRules)}
            {automaticRules.pausedCount > 0 && (
                <>
                    {" "}
                    <Button variant="link" size="sm" onClick={openPausedRule}>
                        Review {pluralize("rule", automaticRules.pausedCount)}
                    </Button>
                </>
            )}
        </span>
    );
}
